import React, { useState } from 'react';
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Checkbox,
  Typography,
  Button,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorOutline } from '@mui/icons-material';
import { Alert } from '@mui/material';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { BaseCurrencies } from '@joindeed/currency';

import {
  usePendingPayrollRunsQuery,
  useCreateInvoiceForPayrollMutation,
  usePayrollPayGroupsQuery,
  CreateInvoiceForPayrollMutationVariables,
  DonationDisbursementType,
} from 'generated/graphql';
import { currencyFormat } from 'utils/currencyFormat';
import { selectUser } from 'entities/user/selectors';
import { showErrorAction, showMessageAction } from 'containers/Notification/actions';
import { PaygroupDropdown } from 'containers/PayrollFlatFilePage/PaygroupDropdown';

import { InvoiceCreationModal } from './InvoiceCreationModal';

export const CreateInvoicePayroll = ({
  getRecommendedPaymentDueDate,
}: {
  getRecommendedPaymentDueDate: () => string;
}) => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const { data, loading, error, refetch } = usePendingPayrollRunsQuery();
  const paygroupsQuery = usePayrollPayGroupsQuery();
  const [createInvoice, { loading: createInvoiceLoading }] = useCreateInvoiceForPayrollMutation({
    update: (cache) => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'invoices' });
      cache.gc();
    },
  });

  const [payrollPayGroupId, setPayrollPayGroupId] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState(getRecommendedPaymentDueDate());

  const paygroups = paygroupsQuery.data?.payrollPayGroups || [];
  const paygroup = paygroups.find((p) => p.id === payrollPayGroupId);

  const payrollRuns = (data?.pendingPayrollRuns?.items || []).filter((payrollRun) =>
    payrollPayGroupId ? payrollRun.payrollPayGroupId === payrollPayGroupId : true
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedIds(event.target.checked ? payrollRuns.map((run) => run.id) : []);

  const handleSelectRow = (selectedId: string) => {
    if (selectedIds.includes(selectedId)) {
      setSelectedIds(selectedIds.filter((id) => id !== selectedId));
    } else {
      setSelectedIds([...selectedIds, selectedId]);
    }
  };

  const handleCreateInvoice = async () => {
    try {
      const variables: Partial<CreateInvoiceForPayrollMutationVariables> & {
        payrollRunIds: string[];
      } = {
        payrollRunIds: selectedIds,
      };

      if (poNumber.trim()) {
        variables.poNumber = poNumber.trim();
      }

      if (paymentDueDate) {
        variables.paymentDueDate = new Date(paymentDueDate).toISOString();
      }

      await createInvoice({
        variables,
      });
      // @TODO-DP: potentially we can update the cache instead of refetching
      void refetch();
      setSelectedIds([]);
      setModalOpen(false);
      setPoNumber('');
      dispatch(showMessageAction('Invoice created successfully.'));
    } catch (e) {
      dispatch(showErrorAction('Failed to create an invoice. Please try again.'));
      console.error(e);
      Sentry.captureException(e);
    }
  };

  const handleOpenModal = () => {
    setPoNumber('');
    setPaymentDueDate(getRecommendedPaymentDueDate());
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPoNumber('');
    setPaymentDueDate(getRecommendedPaymentDueDate());
  };

  if (
    user &&
    !user.organization.disbursementConfiguration?.invoicing[
      DonationDisbursementType.PayrollDonation
    ]
  ) {
    return null;
  }

  if (error) {
    return (
      <Alert icon={<ErrorOutline />} severity="error">
        There was an error loading payroll data. Please try again.
      </Alert>
    );
  }

  const selectedCount = selectedIds.length;
  const rowCount = payrollRuns.length;

  const generateInvoiceButton = (
    <Button
      variant="contained"
      color="primary"
      disabled={createInvoiceLoading || selectedCount === 0}
      onClick={handleOpenModal}
    >
      Generate Payroll Invoice ({selectedCount})
    </Button>
  );

  const confirmationModal = (
    <InvoiceCreationModal
      open={modalOpen}
      onClose={handleCloseModal}
      onConfirm={handleCreateInvoice}
      selectedCount={selectedCount}
      poNumber={poNumber}
      onPoNumberChange={setPoNumber}
      paymentDueDate={paymentDueDate}
      onPaymentDueDateChange={setPaymentDueDate}
      isLoading={createInvoiceLoading}
      donationDisbursementType={DonationDisbursementType.PayrollDonation}
    />
  );

  return (
    <Box mb={4}>
      <h2>Payroll</h2>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        {(paygroups.length || 0) > 1 && paygroups && (
          <PaygroupDropdown
            payrollPayGroups={paygroups}
            payrollPayGroupId={payrollPayGroupId}
            onChange={setPayrollPayGroupId}
          />
        )}
      </Box>
      <Paper style={{ overflow: 'hidden' }}>
        <TableContainer>
          <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
            <Typography variant="subtitle1" component="div" style={{ fontWeight: 500 }}>
              Fully Deducted Payroll Pay Cycles
            </Typography>
            {generateInvoiceButton}
          </Box>
          <Table aria-label="fully deducted payroll pay cycles table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedCount > 0 && selectedCount < rowCount}
                    checked={rowCount > 0 && selectedCount === rowCount}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all pay cycles' }}
                  />
                </TableCell>
                <TableCell>Pay Group</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Total Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || paygroupsQuery.loading || createInvoiceLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : payrollRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No pay cycles with invoiceable donations found
                    {payrollPayGroupId ? ` in paygroup ${paygroup?.payGroupDisplayName}` : ''}.
                  </TableCell>
                </TableRow>
              ) : (
                payrollRuns.map((payrollRun) => {
                  const isItemSelected = selectedIds.includes(payrollRun.id);
                  return (
                    <TableRow
                      key={payrollRun.id}
                      hover
                      onClick={() => handleSelectRow(payrollRun.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': `payroll-run-checkbox-${payrollRun.id}`,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        id={`payroll-run-checkbox-${payrollRun.id}`}
                      >
                        {
                          paygroups.find((p) => p.id === payrollRun.payrollPayGroupId)
                            ?.payGroupDisplayName
                        }
                      </TableCell>
                      <TableCell>
                        {format(
                          new Date(payrollRun.startDateExternal),
                          'MMM dd, yyyy'
                        ).toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {format(new Date(payrollRun.endDateExternal), 'MMM dd, yyyy').toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {currencyFormat(payrollRun.total, payrollRun.currencyCode)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              <TableRow>
                <TableCell colSpan={5} align="right">
                  Total Pending Payroll Deductions:{' '}
                  {currencyFormat(
                    payrollRuns.reduce((acc, run) => acc + run.total, 0),
                    // @NOTE-DP: we assume that all payroll goes into the same account and that the currency is the same
                    payrollRuns[0]?.currencyCode || BaseCurrencies.USD
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {confirmationModal}
    </Box>
  );
};
